"use client"

import * as React from "react"
import { animate, splitText, stagger } from "animejs"

import { whenInView, type RevealTrigger } from "./reveal"
import { useMotionScope } from "../../hooks/use-motion-scope"
import { blur, duration, ease, staggerStep } from "../../lib/motion"

type TextRevealElement = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span"

type TextRevealProps = Omit<React.HTMLAttributes<HTMLElement>, "children"> & {
  children: string
  as?: TextRevealElement
  by?: "words" | "chars"
  trigger?: RevealTrigger
  delay?: number
}

function TextReveal(props: TextRevealProps) {
  return <SplitTextReveal key={props.children} {...props} />
}

function SplitTextReveal({ as: Component = "span", by = "words", trigger = "mount", delay = 0, ...props }: TextRevealProps) {
  const { root } = useMotionScope<HTMLElement>(
    ({ root, reduced, scope }) => {
      if (reduced) {
        root.dataset.motionState = "done"
        return
      }
      let split: ReturnType<typeof splitText> | undefined
      const play = () =>
        scope.execute(() => {
          split = splitText(root, { [by]: true, accessible: true })
          const pieces = by === "words" ? split.words : split.chars
          root.dataset.motionState = "done"
          animate(pieces, {
            opacity: [0, 1],
            translateY: ["0.35em", "0em"],
            filter: [`blur(${blur.md}px)`, "blur(0px)"],
            duration: duration.slow,
            delay: stagger(by === "words" ? staggerStep.base : staggerStep.tight, { start: delay }),
            ease: ease.enter,
          })
        })
      const stopWatching = trigger === "mount" ? (play(), undefined) : whenInView(root, play)
      return () => {
        stopWatching?.()
        split?.revert()
      }
    },
    [by, trigger, delay]
  )

  return React.createElement(Component, {
    ref: root,
    "data-slot": "text-reveal",
    "data-motion": "reveal",
    ...props,
  })
}

export { TextReveal }
